import React, { useState } from 'react';
import styled from 'styled-components';
import { setBreakdownGroupBinning } from '../../../../../../redux/slices/oldBuilder';
import {
  BucketDefType,
  BucketKind,
  IFieldFieldsFragment,
  PermissionKey,
} from '../../../../../../utils/apollo/graphqlSchema';
import {
  BinningDisplay,
  BinningPopover,
  convertToManualRangeBinning,
  isValidAutoRangeBucketInput,
  isValidBinning,
} from '../../../../BuilderFields/BuilderBinning';
import { IBinning } from '../../../../../../redux/slices/oldBuilder/binning/types';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { MaybeNil } from '../../../../../../utils/interfaces';
import { IconWrapper, SvgIcon } from '../../../../../Icon';
import { useActionsVisibilityContext } from '../../../../../ActionsVisibilityContext';
import usePermission from '../../../../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../../../../../pages/PermissionControls/PermissionElementWrapper';
import { activeSchemaIdSelector } from '../../../../../../redux/selectors/builder';
import {
  KubitPopover,
  KubitPopoverContent,
  KubitPopoverTrigger,
} from '../../../../../KubitPopover';
import { SquareRemoveButton } from '../../../../../Button';
import KubitTooltip from '../../../../../KubitTooltip';

interface IProps {
  groupIndex: number;
  binning: MaybeNil<IBinning>;
  field: IFieldFieldsFragment;
}

const defaultBucketKinds = [BucketKind.Enum];

const Container = styled.div`
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
`;

export default ({ groupIndex, binning, field }: IProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const [cachedBinning, setCachedBinning] = useState<MaybeNil<IBinning>>(null);
  const { checkIsPermitted } = usePermission();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const isPermittedToUseBreakdownBinning = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.FieldRead,
      PermissionKey.BucketDefRead,
    ],
    schemaId: activeSchemaId,
  });

  const dispatch = useAppDispatch();

  const { hideActions } = useActionsVisibilityContext();

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);

    dispatch(
      setBreakdownGroupBinning({
        groupIndex,
        newBinning: cachedBinning ? cachedBinning : null,
      }),
    );
  };

  const handlePopoverOpen = () => {
    setIsPopoverOpen(true);

    // store binning into cache to allow fallback if cancel
    setCachedBinning(binning);

    hideActions();
  };

  const handleSubmit = () => {
    if (
      binning &&
      binning.type === BucketDefType.Basic &&
      binning.kind === BucketKind.Range &&
      binning.isAutoRangeBucket
    ) {
      const autoRangeBucket = binning.autoRangeBucket;
      if (
        isValidAutoRangeBucketInput(
          autoRangeBucket.min,
          autoRangeBucket.max,
          autoRangeBucket.binCount,
        )
      ) {
        dispatch(
          setBreakdownGroupBinning({
            groupIndex,
            newBinning: convertToManualRangeBinning(binning, false),
          }),
        );
      }
    }

    setCachedBinning(null);
    setIsPopoverOpen(false);
  };

  const handleRemove = () => {
    dispatch(
      setBreakdownGroupBinning({
        groupIndex,
        newBinning: null,
      }),
    );

    setCachedBinning(null);
  };

  const validBinning = isValidBinning(binning);

  const displayBinning = Boolean(!isPopoverOpen && validBinning);

  const supportedBucketKinds = field?.bucketKinds?.length
    ? field.bucketKinds
    : defaultBucketKinds;

  return (
    <KubitPopover isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Container>
        <KubitTooltip content="Binning" placement="bottom" noOpenDelay={true}>
          <PermissionElementWrapper
            isPermitted={isPermittedToUseBreakdownBinning}
          >
            <KubitPopoverTrigger asChild={true}>
              <IconWrapper
                onClick={
                  isPermittedToUseBreakdownBinning
                    ? handlePopoverOpen
                    : undefined
                }
              >
                <SvgIcon
                  name="histogram"
                  width={16}
                  height={16}
                  isActive={isPopoverOpen || displayBinning}
                  isDisabled={!isPermittedToUseBreakdownBinning}
                  customFill="currentColor"
                />
              </IconWrapper>
            </KubitPopoverTrigger>
          </PermissionElementWrapper>
        </KubitTooltip>
        {displayBinning && (
          <>
            <BinningDisplay binning={binning} />
            <SquareRemoveButton
              onClick={handleRemove}
              content={'Remove Binning'}
            />
          </>
        )}
      </Container>
      <KubitPopoverContent>
        <BinningPopover
          isOpen={isPopoverOpen}
          onClose={handlePopoverClose}
          type="breakdownBinning"
          supportedBucketKinds={supportedBucketKinds}
          selectedFieldKey={field.key}
          groupIndex={groupIndex}
          defaultNamedBucketDef={
            cachedBinning?.type === BucketDefType.Named
              ? cachedBinning.namedBucketDef
              : null
          }
          validBinning={validBinning}
          onSubmit={handleSubmit}
        />
      </KubitPopoverContent>
    </KubitPopover>
  );
};
